import { useMemo } from "react";
import {
  useGetAverageProductSummary,
  useGetProductSummariesForNodes,
  useGetProductSummaryHistoryById
} from "../../../../../api/productSummary";
import { EnvironmentalFootprintCards } from "../../../../../components/environmentalFootprint";
import { Skeleton } from "../../../../../components/ui/common/skeleton";
import { NOT_APPLICABLE } from "../../../../../config/constants";
import { ROLES } from "../../../../../config/permission-maps";
import { getUserRolesInLocal } from "../../../../../utils/cacheStorage";
import { humanize } from "../../../../../utils/utils";
import { ProcessingImpactsCard } from "../../../../productSummary/components";
import { formatCalculatedDate, processLifecycleStages } from "../../../../productSummary/helpers";
import { useProcessingSteps } from "../../../../productSummary/hooks/useProductSummary";
import { ImpactData } from "../../../../productSummary/types";
import { ProductDetailsViewModel } from "../../types/productDetailsView.types";

interface EnvironmentalFootprintTabProps {
  vm: ProductDetailsViewModel;
}

/**
 * "Environmental Footprint" tab — renders the shared `EnvironmentalFootprintCards`
 * layout (src/components/environmentalFootprint/), the same one used by the
 * ingredient details tab and the `ProductSummary` page, replacing the legacy
 * `ProductImpactTab`-style table/select-dropdown design. The "Calculated on"
 * selector was dropped: the dedicated History tab already covers browsing
 * past calculations, and the prototype's own Environmental Footprint tab
 * always shows the latest snapshot — this tab now does the same, defaulting
 * to the newest entry in `vm.productSummaryDropdownData` for non-average
 * products.
 */
const EnvironmentalFootprintTab = ({ vm }: EnvironmentalFootprintTabProps) => {
  const localUserRole = getUserRolesInLocal();
  const checkIsUserSupplier = localUserRole?.[0] === ROLES.supplier;

  const productDetails = vm.productDetails;
  const productId = productDetails?._id ?? "";
  const productIsAverage = vm.productIsAverage;

  const latestProductSummaryId = vm.productSummaryDropdownData?.data?.[0]?._id ?? "";
  const latestCalculatedDate = formatCalculatedDate(vm.productSummaryDropdownData?.data?.[0]?.createdAt);

  const { data: historicalData, isFetching: isProductSummaryDataLoading } = useGetProductSummaryHistoryById(
    { productSummaryId: latestProductSummaryId },
    { enabled: Boolean(latestProductSummaryId) && !productIsAverage }
  );

  const { data: averageData, isFetching: isAverageProductSummaryDataLoading } = useGetAverageProductSummary(
    { productId, productIsAverage },
    { enabled: Boolean(productId) && Boolean(productIsAverage) }
  );

  const productSummary = productIsAverage ? averageData : historicalData;

  const { data: productSummariesForNode } = useGetProductSummariesForNodes(latestProductSummaryId);
  const processingSteps = useProcessingSteps(productSummariesForNode);

  const lifecycleStages = useMemo(() => {
    const cycleStageData = productSummary?.cycleStages || {};
    return processLifecycleStages(cycleStageData, NOT_APPLICABLE, humanize);
  }, [productSummary]);

  if (isProductSummaryDataLoading || isAverageProductSummaryDataLoading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-3">
        <Skeleton className="tw-h-40 tw-w-full" />
        <Skeleton className="tw-h-64 tw-w-full" />
      </div>
    );
  }

  if (checkIsUserSupplier) {
    return (
      <p className="tw-rounded-md tw-border tw-border-amber-300 tw-bg-amber-50 tw-px-4 tw-py-3 tw-text-sm tw-text-amber-800">
        Calculated: Upgrade to see results
      </p>
    );
  }

  if (!productSummary) {
    return (
      <p className="tw-rounded-md tw-border tw-border-amber-300 tw-bg-amber-50 tw-px-4 tw-py-3 tw-text-sm tw-text-amber-800">
        Data collection in progress
      </p>
    );
  }

  const { measure: climateChangeMeasure, unit: climateChangeUnit } = productSummary?.climateChange || {};
  const totalImpact = (productSummary?.totalImpact || []) as unknown as ImpactData[];

  return (
    <EnvironmentalFootprintCards
      productImage={productDetails?.product_image_path}
      productName={productDetails?.product_name}
      calculatedDate={latestCalculatedDate}
      isAverageProduct={productIsAverage}
      climateChangeMeasure={climateChangeMeasure}
      climateChangeUnit={climateChangeUnit}
      lifecycleStages={lifecycleStages}
      totalImpact={totalImpact}
      compact
      wrapImpactGrid
      middleContent={
        processingSteps.length > 0 ? <ProcessingImpactsCard processingSteps={processingSteps} /> : undefined
      }
    />
  );
};

export default EnvironmentalFootprintTab;
